// SummonBanner.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The scene QR layer — the ONE renderer for on-screen ordering QRs, and the
// summon escalation in the same skin. Every scene mounts exactly one of these
// INSIDE its live-feed container (griddle window / reel region), so the QR
// always reads as living on the pancake-art feed and can never cover the top
// bar, cards, spotlight, flash band, or the IG host-bar/comment zones — the
// mount point guarantees it structurally.
//
// What renders is decided by resolveQrFlow(scene, shift, config) — see
// utils/qrFlow.js for the flow model (off / free / paid / custom), the
// legacy-gate reconstruction, and the incentive notes. Intensity:
//
//   escalating (shift.qrFlows set by the operator, or a test shift):
//     full   (0 in line) → big ambient banner on the feed
//     nudge  (1–6)       → small corner QR tile
//     silent (7+)        → nothing (you're slammed — don't summon more)
//     EXCEPT paid voice, which floors at nudge instead of going silent: the
//     market QR doubles as the booth's living price menu (walk-up guests need
//     prices even when the line is deep), so it holds the corner tile all
//     shift. Closing the queue is the operator's real "stop summoning" lever.
//   steady (legacy gates, or summonEnabled:false):
//     idle scenes → the full card, always (matches the old JoinCtaCard)
//     booth/main  → the corner tile, always (matches the old OrderQrCard)
//
// URL previews (kept): ?summonPreview=full|nudge|silent pins an intensity in
// any scene at any time; ?summonMode=free|paid pins the voice.
//
// QR colors stay literal (#1d52a0 on #fff) — scannability, never themed.
// The breathe animation runs ONLY on the escalating full banner (empty-queue
// moments), never on a steady all-shift surface — OBS compositor budget.
// ─────────────────────────────────────────────────────────────────────────────
import { QRCodeSVG } from "qrcode.react";
import {
  resolveQrFlow,
  readSummonPreview,
  intensityForDepth,
  QR_FLOW_COPY,
  joinUrlFor,
} from "./../../utils/qrFlow";
import { fmtWait } from "./../../utils/overlayFormat";

const D = "var(--jop-display)";

// `code` = the booth code, rendered under the QR ONLY while a second queue is
// live (BoothCode returns null otherwise). It does double duty: a guest whose
// camera won't scan can type it on /join, and the operator can see at a glance
// that this screen is bound to the right booth.
function QrTile({ url, px, code }) {
  return (
    <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          padding: "9px",
          width: `${px}px`,
          height: `${px}px`,
          boxSizing: "border-box",
          display: "flex",
        }}
      >
        <QRCodeSVG
          value={url}
          size={256}
          bgColor="#ffffff"
          fgColor="#1d52a0"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
      {code && <BoothCodeCaption code={code} />}
    </div>
  );
}

// Understated on purpose: this is on a guest-facing screen, and on an ordinary
// one-queue day it must not appear at all.
function BoothCodeCaption({ code }) {
  return (
    <div
      style={{
        font: `700 0.8rem/1 ${D}`,
        letterSpacing: "0.22em",
        color: "#fff",
        background: "rgba(var(--jop-ink-rgb), 0.45)",
        border: "1px solid rgba(255,255,255,0.3)",
        borderRadius: "7px",
        padding: "4px 9px",
        whiteSpace: "nowrap",
      }}
    >
      {code}
    </div>
  );
}

// ── Price menu rows (paid voice) — name left, price right ───────────────────
// `light` = white-card context (corner tile, navy ink); default renders on the
// orange/pink banner gradient in white.
function MenuRows({ menu, portrait, light }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: light ? "0.15rem" : "0.25rem" }}>
      {menu.map((row) => (
        <div
          key={row.name}
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: "0.8rem",
            fontWeight: 700,
            fontSize: light
              ? portrait
                ? "1.1rem"
                : "0.85rem"
              : portrait
                ? "1.45rem"
                : "1.1rem",
            color: light ? "var(--jop-navy)" : "#fff",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ opacity: light ? 1 : 0.95 }}>
            {row.icon} {row.name}
          </span>
          <span
            style={{
              fontWeight: 900,
              color: light ? "var(--jop-orange)" : "#fff",
              textShadow: light ? "none" : "0 1px 3px rgba(120,20,40,0.45)",
            }}
          >
            {row.price}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Big ambient banner (full) — anchored to the feed bottom ─────────────────
function FullBanner({ copy, url, portrait, queueDepth, waitMin, animate, boothCode }) {
  return (
    <div
      style={{
        position: "absolute",
        left: portrait ? "3vw" : "2vw",
        right: portrait ? "3vw" : "auto",
        bottom: portrait ? "2vh" : "3vh",
        maxWidth: portrait ? "none" : "620px",
        display: "flex",
        alignItems: "center",
        gap: portrait ? "1.6rem" : "1.3rem",
        padding: portrait ? "1.4rem 1.6rem" : "1.1rem 1.3rem",
        borderRadius: "22px",
        background:
          "linear-gradient(150deg, var(--jop-orange), var(--jop-pink) 96%)",
        boxShadow:
          "0 -6px 30px rgba(var(--jop-ink-rgb), 0.35), inset 0 1px 0 rgba(255,255,255,0.35)",
        fontFamily: D,
        color: "#fff",
        animation: animate ? "jopSummonBreathe 3.4s ease-in-out infinite" : "none",
      }}
    >
      <QrTile url={url} px={portrait ? 186 : 168} code={boothCode} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 900,
            fontSize: portrait ? "2.6rem" : "2.2rem",
            lineHeight: 1.02,
            letterSpacing: "0.01em",
            textShadow: "0 1px 6px rgba(120,20,40,0.4)",
          }}
        >
          {copy.head}
        </div>
        <div
          style={{
            fontSize: portrait ? "1.6rem" : "1.2rem",
            fontWeight: 700,
            marginTop: "0.4rem",
            opacity: 0.97,
          }}
        >
          {copy.sub}
        </div>
        {copy.menu && (
          <div
            style={{
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.4)",
              padding: portrait ? "0.55rem 0.9rem" : "0.45rem 0.8rem",
              borderRadius: "14px",
              marginTop: "0.7rem",
            }}
          >
            <MenuRows menu={copy.menu} portrait={portrait} />
          </div>
        )}
        {queueDepth > 0 && (
          <div
            style={{
              fontSize: portrait ? "1.25rem" : "0.95rem",
              fontWeight: 600,
              marginTop: "0.5rem",
              opacity: 0.85,
              letterSpacing: "0.03em",
            }}
          >
            {queueDepth} in queue right now
            {waitMin != null ? ` · est. wait ${fmtWait(waitMin)}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Corner QR tile (nudge / steady booth card) ──────────────────────────────
function CornerTile({ copy, url, portrait, boothCode }) {
  const qr = portrait ? 150 : 132;
  return (
    <div
      style={{
        position: "absolute",
        // Portrait right inset clears the Reels/TikTok icon rail (~130px) so
        // the tile stays scannable in reposted clips too.
        right: portrait ? "150px" : "2vw",
        bottom: "2vh",
        background: "#fff",
        borderRadius: "16px",
        padding: "0.7rem",
        boxShadow: "0 8px 24px rgba(var(--jop-ink-rgb), 0.4)",
        textAlign: "center",
        fontFamily: D,
      }}
    >
      <div
        style={{ width: `${qr}px`, height: `${qr}px`, display: "flex", margin: "0 auto" }}
      >
        <QRCodeSVG
          value={url}
          size={256}
          bgColor="#ffffff"
          fgColor="#1d52a0"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
      <div
        style={{
          color: "var(--jop-navy)",
          fontWeight: 800,
          fontSize: portrait ? "1.3rem" : "0.95rem",
          lineHeight: 1.05,
          margin: "0.45rem auto 0",
        }}
      >
        {copy.tileCap}
      </div>
      {/* White-card context, so the code inverts to navy-on-tint rather than
          the banner's white-on-ink treatment. */}
      {boothCode && (
        <div
          style={{
            font: `700 0.78rem/1 ${D}`,
            letterSpacing: "0.22em",
            color: "var(--jop-navy)",
            background: "rgba(var(--jop-navy-rgb), 0.1)",
            border: "1px solid rgba(var(--jop-navy-rgb), 0.22)",
            borderRadius: "7px",
            padding: "4px 9px",
            margin: "0.4rem auto 0",
            display: "inline-block",
          }}
        >
          {boothCode}
        </div>
      )}
      {copy.menu && (
        <div
          style={{
            marginTop: "0.45rem",
            paddingTop: "0.4rem",
            borderTop: "1px solid rgba(var(--jop-navy-rgb), 0.18)",
            textAlign: "left",
          }}
        >
          <MenuRows menu={copy.menu} portrait={portrait} light />
        </div>
      )}
    </div>
  );
}

// Mount INSIDE the scene's positioned feed container. Fills it with a
// pointer-transparent layer; children anchor to the feed's bottom edge.
export default function SummonBanner({
  scene,
  shift = null,
  // Number of queues live right now (from useCurrentShiftOrders). The booth
  // code renders only when it's >1 — on a solo market there is nothing to
  // disambiguate and the guest-facing screen stays exactly as it was.
  concurrentCount = 0,
  streamConfig = null,
  queueDepth = 0,
  waitMin = null,
  portrait = false,
  active = true,
}) {
  const { forced, mode: modeOverride } = readSummonPreview();
  const resolved = resolveQrFlow(scene, shift, streamConfig);

  let { flow, url, copy } = resolved;
  if (modeOverride) {
    flow = modeOverride;
    copy = QR_FLOW_COPY[modeOverride];
    url = joinUrlFor(shift);
  }

  let intensity = null;
  let animate = false;
  if (forced) {
    intensity = forced;
    if (flow === "off") {
      // Forced preview in a scene whose flow is off — default to paid voice.
      copy = copy || QR_FLOW_COPY.paid;
      url = url || joinUrlFor(shift);
    }
    animate = intensity === "full" && active;
  } else if (flow === "off") {
    return null;
  } else if (resolved.escalate) {
    intensity = intensityForDepth(queueDepth);
    // Paid voice never goes silent — the tile is the market's living price
    // menu (see header). ?summonPreview=silent still forces silence.
    if (intensity === "silent" && flow === "paid") intensity = "nudge";
    animate = intensity === "full" && active;
  } else {
    // Steady legacy surface — mirror exactly where the old surfaces lived
    // (no new QR appears on a legacy shift that didn't show one before):
    // Booth had OrderQrCard in both orientations → quiet corner tile.
    // Break/StartingSoon had the portrait JoinCtaCard → full card, portrait
    // only; StartingSoon landscape had the custom-URL card → tile. No
    // animation — these can stay on screen for a whole shift.
    if (scene === "booth") intensity = "nudge";
    else if (scene === "break") intensity = portrait ? "full" : null;
    else if (scene === "startingSoon")
      intensity = portrait ? "full" : flow === "custom" ? "nudge" : null;
    else intensity = null;
  }

  if (!intensity || intensity === "silent") return null;

  // The code is a disambiguator, so it appears only when there IS ambiguity:
  // a second queue live AND a code to show. On a solo market this is null and
  // the banner renders exactly as it always has.
  const boothCode = concurrentCount > 1 ? (shift?.joinCode || null) : null;

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 6 }}>
      {intensity === "nudge" ? (
        <CornerTile copy={copy} url={url} portrait={portrait} boothCode={boothCode} />
      ) : (
        <FullBanner
          copy={copy}
          url={url}
          portrait={portrait}
          queueDepth={queueDepth}
          waitMin={waitMin}
          animate={animate}
          boothCode={boothCode}
        />
      )}
    </div>
  );
}
